import React,{useState,useEffect,useRef} from 'react';
import {useGame} from './hooks/useGame';
import Board from './components/Board';
import Header from './components/Header';
import Footer from './components/Footer';
import GalleryModal from './components/modals/GalleryModal';
import AchievementsModal from './components/modals/AchievementsModal';
import AchievementNotification from './components/AchievementNotification';
import Modal from './components/modals/Modal';
import {TILE_CHARACTERS,TUTORIAL_SECTIONS} from './utils/constants';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './styles/Cute2048.css';

function App() {
  const {
    grid,
    score,
    bestScore,
    status,
    stats,
    unlockedTiles,
    unlockedAchievements,
    achievementNotifications,
    nextValue,
    move,
    resetGame,
    continueGame
  }=useGame();
  const [activeModal,setActiveModal]=useState(null);
  const touchStart=useRef(null);
  const isSwiping=useRef(false);
  const mouseStart=useRef(null);
  const isMouseDragging=useRef(false);

  useEffect(()=> {
    const params=new URLSearchParams(window.location.search);
    const action=params.get('action');
    if (action==='new') {
      resetGame();
    } else if (action==='gallery') {
      setActiveModal('gallery');
    } else if (action==='achievements') {
      setActiveModal('achievements');
    }
    if (action) {
      window.history.replaceState({},'','/');
    }
  },[]);

  const handleTouchStart=(e)=> {
    if (e.target.closest('button') || e.target.closest('[role="dialog"]')) return;
    touchStart.current={
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };
    isSwiping.current=false;
  };

  const handleTouchMove=(e)=> {
    if (!touchStart.current) return;
    const dx=e.touches[0].clientX - touchStart.current.x;
    const dy=e.touches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      isSwiping.current=true;
      e.preventDefault();
    }
  };

  const handleTouchEnd=(e)=> {
    if (!touchStart.current || !isSwiping.current) {
      touchStart.current=null;
      return;
    }
    const dx=e.changedTouches[0].clientX - touchStart.current.x;
    const dy=e.changedTouches[0].clientY - touchStart.current.y;
    const duration=Date.now() - touchStart.current.time;
    if (duration < 600) {
      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) > 40) move(dx > 0 ? 'right' : 'left');
      } else if (Math.abs(dy) > 40) {
        move(dy > 0 ? 'down' : 'up');
      }
    }
    touchStart.current=null;
    isSwiping.current=false;
  };

  const handleMouseDown=(e)=> {
    if (e.target.closest('button') || e.target.closest('[role="dialog"]')) return;
    mouseStart.current={x: e.clientX,y: e.clientY,time: Date.now()};
    isMouseDragging.current=false;
  };

  const handleMouseMove=(e)=> {
    if (!mouseStart.current) return;
    const dx=e.clientX - mouseStart.current.x;
    const dy=e.clientY - mouseStart.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      isMouseDragging.current=true;
      e.preventDefault();
    }
  };

  const handleMouseUp=(e)=> {
    if (!mouseStart.current || !isMouseDragging.current) {
      mouseStart.current=null;
      return;
    }
    const dx=e.clientX - mouseStart.current.x;
    const dy=e.clientY - mouseStart.current.y;
    const duration=Date.now() - mouseStart.current.time;
    if (duration < 800) {
      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) > 50) move(dx > 0 ? 'right' : 'left');
      } else if (Math.abs(dy) > 50) {
        move(dy > 0 ? 'down' : 'up');
      }
    }
    mouseStart.current=null;
    isMouseDragging.current=false;
  };

  useEffect(()=> {
    const handleKeyDown=(e)=> {
      if (activeModal) return;
      if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)) {
        e.preventDefault();
        move(e.key.replace('Arrow','').toLowerCase());
      }
    };
    window.addEventListener('keydown',handleKeyDown);
    return ()=> window.removeEventListener('keydown',handleKeyDown);
  },[move,activeModal]);

  useEffect(()=> {
    if (score > 0) {
      document.title=`萌趣方塊 2048 - 分數: ${score.toLocaleString()} | 最高: ${bestScore.toLocaleString()} | Homy Tech`;
    } else {
      document.title='萌趣方塊 2048 - 免費線上可愛動物益智遊戲 | Homy Tech 2026';
    }
    const ogDesc=document.querySelector('meta[property="og:description"]');
    if (ogDesc && score > 0) {
      ogDesc.setAttribute(
        'content',
        `我在萌趣方塊 2048 獲得了 ${score.toLocaleString()} 分!最高方塊: ${TILE_CHARACTERS[stats.maxTile]} (${stats.maxTile})。一起來挑戰吧!`
      );
    }
  },[score,bestScore,stats.maxTile]);

  useEffect(()=> {
    const script=document.querySelector('script[type="application/ld+json"]');
    if (script && stats.moves > 0) {
      try {
        const data=JSON.parse(script.textContent);
        if (data['@type']==='VideoGame') {
          data.interactionStatistic.userInteractionCount=(125000 + stats.moves).toString();
          script.textContent=JSON.stringify(data);
        }
      } catch (e) {
        console.error('Failed to update structured data:',e);
      }
    }
  },[stats.moves]);

  return (
    <div
      className="min-h-[100dvh] w-full flex flex-col lg:flex-row items-center justify-center p-2 sm:p-3 lg:p-8 relative overflow-hidden bg-[#fff9e9]"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{touchAction: 'none',cursor: isMouseDragging.current ? 'grabbing' : 'grab'}}
    >
      <div className="bg-clouds overflow-hidden">
        <div className="absolute w-64 h-24 top-[10%] left-[-5%] bg-white/40 rounded-full blur-2xl"></div>
        <div className="absolute w-96 h-32 bottom-[10%] right-[-10%] bg-pink-200/20 rounded-full blur-3xl"></div>
      </div>

      <AchievementNotification notifications={achievementNotifications} />

      <div className="hidden lg:flex flex-col gap-6 w-[280px] items-end pr-10 z-10">
        <div className="text-right">
          <h1 className="text-5xl font-black text-[#ff7e5f] mb-1 leading-tight drop-shadow-sm">
            萌趣方塊<br />2048 🐱
          </h1>
          <p className="text-orange-400 font-bold tracking-widest text-xs">
            CUTE PUZZLE ADVENTURE
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-lg rounded-[32px] p-8 shadow-xl border-b-8 border-blue-100 flex flex-col items-center w-full">
          <span className="text-xs font-black text-blue-400 mb-4 tracking-widest uppercase">
            下一個角色
          </span>
          <div className="text-7xl animate-bounce mb-3">
            {TILE_CHARACTERS[nextValue]}
          </div>
        </div>

        <button
          onClick={()=> setActiveModal('gallery')}
          className="w-full bg-white/90 hover:bg-white p-5 rounded-3xl text-[#ff7e5f] shadow-lg transition-all flex items-center justify-center gap-3 font-black text-lg border-b-4 border-orange-100 active:translate-y-1"
          aria-label="開啟角色圖鑑"
        >
          <SafeIcon icon={FiIcons.FiBook} className="text-2xl" />
          角色圖鑑
        </button>
      </div>

      <div className="flex flex-col items-center z-10 w-full lg:w-auto max-w-[500px] lg:max-w-none">
        <div className="lg:hidden w-full px-3 pt-2 pb-2">
          <Header
            score={score}
            bestScore={bestScore}
            openGallery={()=> setActiveModal('gallery')}
            openHelp={()=> setActiveModal('help')}
            openAchievements={()=> setActiveModal('achievements')}
            resetGame={resetGame}
            nextValue={nextValue}
          />
        </div>

        <main className="flex items-center justify-center w-full py-3 lg:py-0" role="main" aria-label="遊戲棋盤">
          <Board grid={grid} />
        </main>
        <div className="lg:hidden h-20 w-full"></div>
      </div>

      <div className="hidden lg:flex flex-col gap-6 w-[280px] items-start pl-10 z-10">
        <div className="w-full space-y-6">
          <ScoreBox label="目前得分" value={score} color="border-orange-200" titleColor="text-orange-400" />
          <ScoreBox label="最高紀錄" value={bestScore} color="border-yellow-200" titleColor="text-yellow-500" />
        </div>

        <div className="flex flex-col gap-4 w-full mt-4">
          <SidebarBtn onClick={resetGame} icon={FiIcons.FiRefreshCcw} label="重新開始" color="bg-red-400" aria="重新開始遊戲" />
          <SidebarBtn onClick={()=> setActiveModal('achievements')} icon={FiIcons.FiAward} label="成就獎勵" color="bg-yellow-400" aria="查看成就獎勵" />
          <SidebarBtn onClick={()=> setActiveModal('help')} icon={FiIcons.FiHelpCircle} label="遊玩教學" color="bg-blue-400" aria="查看遊玩教學" />
        </div>
      </div>

      <Footer />

      <Modal isOpen={status==='over'} onClose={resetGame} title="遊戲結束">
        <div className="text-center p-4">
          <div className="text-7xl mb-6" role="img" aria-label="難過表情">😿</div>
          <p className="text-2xl mb-8 font-black text-gray-700">
            最終分數: {score.toLocaleString()}
          </p>
          <button
            onClick={resetGame}
            className="w-full bg-[#ff7e5f] text-white py-5 rounded-3xl font-black text-xl shadow-xl active:scale-95 transition-all"
            aria-label="重新開始遊戲"
          >
            再試一次
          </button>
        </div>
      </Modal>

      <Modal isOpen={status==='won'} onClose={continueGame} title="太棒了!">
        <div className="text-center p-4">
          <div className="text-7xl mb-6" role="img" aria-label="皇冠">👑</div>
          <p className="text-xl mb-8 font-bold text-gray-700">你成功合成了 2048!</p>
          <button
            onClick={continueGame}
            className="w-full bg-green-400 text-white py-5 rounded-3xl font-black text-xl shadow-lg active:scale-95 transition-all"
            aria-label="繼續挑戰"
          >
            繼續挑戰
          </button>
        </div>
      </Modal>

      <GalleryModal
        isOpen={activeModal==='gallery'}
        onClose={()=> setActiveModal(null)}
        unlockedTiles={unlockedTiles}
      />

      <AchievementsModal
        isOpen={activeModal==='achievements'}
        onClose={()=> setActiveModal(null)}
        stats={stats}
        maxTile={stats.maxTile}
        score={score}
        unlockedAchievements={unlockedAchievements}
      />

      <Modal
        isOpen={activeModal==='help'}
        onClose={()=> setActiveModal(null)}
        title="如何遊玩萌趣方塊 2048？"
      >
        <div className="space-y-5">
          {TUTORIAL_SECTIONS.map((section)=> (
            <section
              key={section.id}
              className="bg-gradient-to-br from-blue-50 to-purple-50 p-5 rounded-2xl border-2 border-blue-100 shadow-sm"
            >
              <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2 drop-shadow-sm">
                {section.title}
              </h2>
              <div className="space-y-4">
                {section.items.map((item,idx)=> (
                  <div key={idx} className="flex gap-4 items-start bg-white/80 p-4 rounded-xl shadow-sm">
                    <span className="text-3xl flex-shrink-0 drop-shadow-sm" role="img" aria-label={item.title}>
                      {item.icon}
                    </span>
                    <div className="flex-1">
                      <p className="font-black text-gray-800 text-sm mb-2 drop-shadow-sm">
                        {item.title}
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line font-semibold">
                        {item.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </Modal>
    </div>
  );
}

const ScoreBox=({label,value,color,titleColor})=> (
  <div className={`bg-white/70 backdrop-blur-md rounded-[28px] p-6 shadow-lg border-b-8 ${color}`}>
    <p className={`text-xs font-black ${titleColor} uppercase tracking-widest mb-1`}>{label}</p>
    <p className="text-4xl font-black text-gray-700 tabular-nums">{value.toLocaleString()}</p>
  </div>
);

const SidebarBtn=({onClick,icon,label,color,aria})=> (
  <button
    onClick={onClick}
    className={`${color} text-white w-full py-5 rounded-2xl font-black shadow-lg flex items-center justify-center gap-3 hover:brightness-110 active:scale-95 transition-all border-b-4 border-black/10`}
    aria-label={aria || label}
  >
    <SafeIcon icon={icon} className="text-2xl" />
    {label}
  </button>
);

export default App;